import { useEffect, useMemo, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import type { Key } from 'chessground/types';
import { Board, type BoardMoveEvent } from '@/chess/board';
import { KidHint, TurnIndicator } from '@/ui';
import type { DrillPosition, RepertoireDetail } from './types';
import { openingLabApi, type AttemptPayload } from './api';
import { pickUnseenMirror, type MirroredDrill } from './drillPickers';

interface Props {
  repertoire: RepertoireDetail;
  positions: DrillPosition[];
  onResult: (result: 'success' | 'partial' | 'fail') => void;
}

/**
 * Mode **anti-auto-pilot** (T4) — affiche la position du répertoire avec un
 * miroir horizontal (files a↔h, b↔g, ...). Léon doit jouer le coup miroir
 * équivalent. Casse la mémoire purement visuelle, force le raisonnement.
 */
export function DrillMirror({ repertoire, positions, onResult }: Props) {
  // Fix 2026-05-17 : Set in-memory des positions déjà miroir-drilled pour
  // éviter les répétitions consécutives ("ce sont souvent les mêmes
  // exercices" — bug Léon). Clé `fenBefore` de la position originale.
  const seenRef = useRef<Set<string>>(new Set());
  const [drill, setDrill] = useState<MirroredDrill | null>(() => pickUnseenMirror(positions, seenRef.current));
  const [attempts, setAttempts] = useState(0);
  const [phase, setPhase] = useState<'playing' | 'revealed'>('playing');
  // Trace si la position courante a déjà été ratée (= 2/2 essais consommés).
  // Conditionne l'affichage du bouton « Réessayer » même après reset du
  // compteur d'essais. Reset à false quand une nouvelle position est tirée.
  const [hasFailedPosition, setHasFailedPosition] = useState(false);
  // En mode retry on n'envoie plus d'attempt au backend (cf. DrillRecall).
  const [isRetrying, setIsRetrying] = useState(false);
  const startedAtRef = useRef<number>(performance.now());

  useEffect(() => {
    startedAtRef.current = performance.now();
    setAttempts(0);
    setPhase('playing');
    setHasFailedPosition(false);
    setIsRetrying(false);
  }, [drill]);

  const chess = useMemo(() => (drill ? new Chess(drill.mirrored.fen) : new Chess()), [drill]);

  const legalMoves = useMemo(() => {
    if (!drill) return new Map<Key, Key[]>();
    const map = new Map<Key, Key[]>();
    for (const m of chess.moves({ verbose: true })) {
      const list = map.get(m.from as Key) ?? [];
      list.push(m.to as Key);
      map.set(m.from as Key, list);
    }
    return map;
  }, [chess, drill]);

  function pickNext() {
    setDrill(pickUnseenMirror(positions, seenRef.current));
  }

  /**
   * Réessai sur la même position miroir sans la consommer ni biaiser les KPIs.
   * Le fail backend a déjà été loggé dans `handleMove` au moment du 2/2,
   * inutile de l'envoyer à nouveau (et on ne loggue rien pendant le retry).
   */
  function retryPosition(): void {
    startedAtRef.current = performance.now();
    setAttempts(0);
    setPhase('playing');
    setIsRetrying(true);
  }

  function logAttempt(result: AttemptPayload['result'], playedUci?: string) {
    if (!drill) return;
    const payload: AttemptPayload = {
      module: 'opening',
      slug: `${repertoire.slug}:mirror:ply${drill.original.ply}`,
      fen: drill.mirrored.fen,
      side_to_move: drill.original.sideToMove,
      mode: 'mirror',
      result,
      time_spent_ms: Math.round(performance.now() - startedAtRef.current),
      source_ref: repertoire.slug,
      expected_move: drill.mirrored.expectedUci,
      ...(playedUci ? { played_move: playedUci } : {}),
    };
    openingLabApi.postAttempt(payload).catch((e) => console.error('[mirror] log failed', e));
    onResult(result);
  }

  function handleMove({ from, to }: BoardMoveEvent) {
    if (!drill || phase === 'revealed') return;
    // F-5 P1 : try/catch contre chess.js v1 throw sur coup illégal.
    let move;
    try {
      move = chess.move({ from, to, promotion: 'q' });
    } catch {
      return;
    }
    if (!move) return;
    const playedUci = `${move.from}${move.to}${move.promotion ?? ''}`;
    if (playedUci === drill.mirrored.expectedUci) {
      if (!isRetrying) logAttempt('success', playedUci);
      setPhase('revealed');
      return;
    }
    chess.undo();
    const newAttempts = attempts + 1;
    setAttempts(newAttempts);
    if (newAttempts >= 2) {
      if (!isRetrying) {
        logAttempt('fail', playedUci);
        setHasFailedPosition(true);
      }
      setPhase('revealed');
    }
  }

  if (!drill) {
    return <p className="text-ink-300">Aucune position drillable dans ce répertoire.</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <KidHint tone="violet" title="Pourquoi ce drill ?">
        La position est inversée gauche-droite pour casser la mémoire visuelle.
        Tu ne peux plus jouer en pilote auto — tu dois raisonner sur les pièces,
        pas sur les cases.
      </KidHint>
      <header className="flex items-baseline justify-between">
        <p className="text-caption uppercase text-violet-500">
          Miroir · coup {drill.original.ply} · {drill.original.sideToMove === 'white' ? 'Blancs' : 'Noirs'}
        </p>
        <p className="text-body-sm text-ink-300">Essai {attempts + 1} / 2</p>
      </header>

      <p className="text-body-sm text-ink-300">
        Position miroir horizontale : files a↔h, b↔g, c↔f, d↔e. Joue le coup miroir équivalent.
      </p>

      <TurnIndicator state={phase === 'playing' ? 'user-turn' : 'idle'} idleLabel="Coup miroir révélé" />

      <Board
        fen={drill.mirrored.fen}
        orientation={repertoire.color}
        interactive={phase === 'playing'}
        legalMoves={legalMoves}
        turnColor={drill.original.sideToMove}
        onMove={handleMove}
      />

      {phase === 'revealed' && (
        <section className="rounded-xl border border-ink-700 bg-ink-800 p-4">
          <p className="text-body">
            Coup miroir attendu :{' '}
            <strong className="font-mono text-acid-500">{drill.mirrored.expectedUci}</strong>
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {hasFailedPosition && (
              <button
                type="button"
                onClick={retryPosition}
                className="rounded-lg border border-violet-500/60 bg-violet-500/10 px-4 py-2 font-display text-body text-violet-500 transition hover:bg-violet-500/20"
              >
                Réessayer
              </button>
            )}
            <button
              type="button"
              onClick={pickNext}
              className="rounded-lg bg-violet-500 px-4 py-2 font-display text-body text-paper-50 transition hover:opacity-90"
            >
              Position suivante
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

